import {
  Home,
  LogOut,
  PanelLeftClose,
  PanelLeftOpen,
  TrendingUp,
  User,
  UserPlus,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';

import Logo from '@/components/common/Logo';
import { Avatar, Button } from '@/components/ui';
import useAuth from '@/hooks/useAuth';
import cn from '@/utils/cn';
import { ROUTES, SALES_RM_ROLE, STORAGE_KEYS } from '@/utils/constants';

const NAV_ROW_STEP = 48;

interface NavItem {
  to: string;
  label: string;
  icon: typeof User;
  end: boolean;
}

const PROFILE_NAV: NavItem[] = [
  {
    to: ROUTES.PROFILE,
    label: 'Profile',
    icon: User,
    end: false,
  },
];

const SALES_RM_NAV: NavItem[] = [
  {
    to: ROUTES.SALES_RM_HOME,
    label: 'Home',
    icon: Home,
    end: true,
  },
  {
    to: ROUTES.SALES_RM_LEADS,
    label: 'Lead',
    icon: UserPlus,
    end: false,
  },
  {
    to: ROUTES.SALES_RM_ORDERS,
    label: 'Orders',
    icon: TrendingUp,
    end: false,
  },
  {
    to: ROUTES.PROFILE,
    label: 'Profile',
    icon: User,
    end: false,
  },
];

const MainLayout = () => {
  const { user, logout } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isSalesRm = user?.jobRole === SALES_RM_ROLE;
  const navItems = useMemo(() => (isSalesRm ? SALES_RM_NAV : PROFILE_NAV), [isSalesRm]);
  const homeRoute = isSalesRm ? ROUTES.SALES_RM_HOME : ROUTES.PROFILE;
  const navSectionLabel = isSalesRm ? 'Workspace' : 'Home';
  const { pathname } = useLocation();
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(STORAGE_KEYS.MAIN_NAV_COLLAPSED) === '1',
  );

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.MAIN_NAV_COLLAPSED, collapsed ? '1' : '0');
  }, [collapsed]);

  const activeIndex = navItems.findIndex(({ to, end }) =>
    end ? pathname === to : pathname === to || pathname.startsWith(`${to}/`),
  );
  const activeSection = navItems[activeIndex]?.label ?? (isSalesRm ? 'Home' : 'Home');

  return (
    <div className="flex min-h-screen bg-white">
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-20 flex w-16 flex-col border-r border-line bg-white transition-[width] duration-200',
          !collapsed && 'lg:w-64',
        )}
      >
        <div className="flex h-16 items-center justify-center border-b border-line px-2 lg:px-3">
          <span className="lg:hidden">
            <Logo to={homeRoute} showWordmark={false} />
          </span>

          <div
            className={cn('hidden w-full lg:flex', collapsed ? 'justify-center' : 'justify-start')}
          >
            <Logo to={homeRoute} showWordmark={!collapsed} />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-2 lg:p-3">
          <p
            className={cn(
              'hidden px-3 pb-2 pt-3 text-[10px] font-bold uppercase tracking-widest text-ink/40',
              !collapsed && 'lg:block',
            )}
          >
            {navSectionLabel}
          </p>
          <nav className="relative flex flex-col gap-1">
            <span
              aria-hidden
              className={cn(
                'absolute inset-x-0 h-11 rounded-lg bg-cream ring-1 ring-line/60 transition-all duration-300 ease-out',
                activeIndex < 0 && 'opacity-0',
              )}
              style={{ transform: `translateY(${Math.max(activeIndex, 0) * NAV_ROW_STEP}px)` }}
            />
            {navItems.map(({ to, label, icon: Icon, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) =>
                  cn(
                    'relative z-10 flex h-11 items-center justify-center gap-2.5 rounded-lg px-3',
                    'text-sm font-semibold transition-colors duration-200',
                    !collapsed && 'lg:justify-start',
                    isActive ? 'text-ink' : 'text-ink/55 hover:text-ink',
                  )
                }
                title={label}
              >
                <Icon className="h-[18px] w-[18px] shrink-0" />
                <span className={cn('hidden', !collapsed && 'lg:inline')}>{label}</span>
              </NavLink>
            ))}
          </nav>
        </div>
      </aside>

      <div
        className={cn(
          'flex min-h-screen flex-1 flex-col pl-16 transition-[padding] duration-200',
          !collapsed && 'lg:pl-64',
        )}
      >
        <header className="sticky top-0 z-10 flex h-16 items-center justify-between border-b border-line bg-white/80 px-4 backdrop-blur-sm sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setCollapsed((c) => !c)}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              className="hidden h-8 w-8 shrink-0 items-center justify-center rounded-lg text-ink/40 transition-colors hover:bg-cream hover:text-ink lg:flex"
            >
              {collapsed ? (
                <PanelLeftOpen className="h-[18px] w-[18px]" />
              ) : (
                <PanelLeftClose className="h-[18px] w-[18px]" />
              )}
            </button>

            <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-sm">
              <span className="font-semibold text-ink/40">{navSectionLabel}</span>
              <span className="text-ink/30">/</span>
              <span className="font-bold text-ink">{activeSection}</span>
            </nav>
          </div>

          <div className="flex items-center gap-3">
            {/* Fix 8: the identity chip is the way to the profile page in every
                shell now, not a menu trigger. This layout already showed Logout
                as its own button, so only the chip needed to become a link. */}
            <Link
              to={ROUTES.PROFILE}
              aria-label={`My profile${user?.name ? ` - ${user.name}` : ''}`}
              className="flex items-center gap-2 rounded-full border border-line bg-white py-1 pl-1 pr-3 shadow-sm transition-colors hover:bg-cream focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
            >
              <Avatar name={user?.name ?? 'User'} size="sm" />
              <div className="hidden text-left sm:block">
                <p className="max-w-[10rem] truncate text-xs font-bold leading-tight text-ink">
                  {user?.name ?? 'User'}
                </p>
                <p className="text-[11px] leading-tight text-brand">
                  {isAdmin ? 'Administrator' : (user?.jobRole ?? 'Member')}
                </p>
              </div>
            </Link>

            <Button
              variant="danger"
              size="sm"
              onClick={() => {
                void logout();
              }}
              className="flex items-center gap-1.5"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </Button>
          </div>
        </header>

        <main className="flex-1 px-4 py-8 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-6xl">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default MainLayout;
